/**
 * The uniform single-pending-choice model (SPEC.md §4.3, CONTEXT.md).
 *
 * A `GameState` always carries exactly one pending `ChoiceRequest`. Every decision — alter
 * your opening hand, take a turn action, pick a challenge target, order your own waiting
 * triggers, and later targets and may/decline — is the same shape, so `legalActions` serves
 * the AI and the UI through one path and the action log is one flat list of keys.
 *
 * ## Canonical action keys (SPEC.md §4.4)
 *
 * The engine owns the keys. Each is stable (same option ⇒ same key, whoever asks, however
 * often) and unique within its request, and doubles as the ISMCTS edge key, the UI option
 * id and the action-log entry. Two rules decide what an option is keyed *by*:
 *
 * - Options over a **public** card (a character in play) key by **instance id** — `i12` —
 *   because instances there are individually meaningful.
 * - Options over a **hidden** card (your hand) key by **card id** — `d12` — because the
 *   opponent may not learn which physical copy you chose, and because two copies of the
 *   same card in hand are the same move. The engine resolves a `d`-key to the first
 *   matching instance in hand order, so duplicates collapse into one option.
 *
 * Options over something that is neither — a waiting bag item — key by that object's own
 * dense id (`b3`), which is assigned in the order triggers entered the bag.
 */
import type { PaymentPlan } from './costs.ts';
import type { InstanceId, PlayerIndex } from './ids.ts';

/** What kind of decision the pending request is. New kinds arrive with later tickets. */
export type ChoiceKind =
  /** Altering the opening hand, CR §2.2.2: bottom a card, or stop. */
  | 'mulligan'
  /** A main-phase turn action, or ending the turn (CR §3.3). */
  | 'turn-action'
  /** Which opposing card the declared challenger challenges (CR §4.6). */
  | 'challenge-target'
  /**
   * Which of *your own* waiting triggered abilities resolves next (CR §7.7.4). Only ever
   * offered to the player holding the bag, only over their own triggers, and only when they
   * have two or more — a lone trigger resolves without asking.
   */
  | 'bag-order'
  /**
   * Which of the eligible characters ready, while a live ready-cap restriction is in effect
   * (CR §6.6.5 — "each opponent can't ready more than N of their characters at the start of
   * their next turn"). Only offered when there are more eligible characters than the cap
   * allows; a cap that never bites (fewer eligible than the cap, or no cap at all) readies
   * everyone without asking, exactly as an uncapped Ready step always has.
   */
  | 'ready-choice'
  /**
   * Which card(s) a resolving ability targets (CR §6.7.3). The options are the selector's
   * pool minus every limiter — Ward included — so an illegal choice is never offered and the
   * CR's rewind-on-illegal-choice never has to fire.
   */
  | 'ability-target'
  /**
   * Which player a resolving ability names (ticket 102) — CR's "chosen player"/"chosen
   * opponent" phrasing, `ability-target`'s player-scoped twin. A `PlayerIndex` is never hidden
   * information (SPEC.md §4.4), so — unlike `ability-target` — nothing about the option list
   * itself needs redacting.
   */
  | 'choose-player'
  /**
   * How much of a shared pool to remove from the character just picked in a
   * `distribute-remove-damage` decision (ticket 135) — the allocation half of "remove up to N
   * damage total from any number of your characters", asked once per picked character, right
   * after `ability-target` names which one.
   */
  | 'ability-amount'
  /** A "you may" (CR §6.1.4), and any other yes/no an effect or a keyword raises. */
  | 'may'
  /** A "choose one —" mode (CR §6.1.3). */
  | 'ability-mode'
  /**
   * A decision a `custom()` generator yielded (SPEC.md §5.2). The escape hatch may write an
   * effect the combinators cannot, but never a decision the option list cannot name: the
   * generator supplies the legal tokens and the engine keys them like any other option.
   */
  | 'custom-choice'
  /**
   * "Choose and discard a card" (CR §7.3) — asked of the *discarding* player, whoever's
   * ability caused it, and keyed by printed id because a hand is hidden.
   */
  | 'discard'
  /** The game is over; there are no options. `isTerminal` is `true`. */
  | 'game-over';

/** The plain-data description of one legal option. */
export type Action =
  /** Put this card from hand on the bottom of your deck while altering (CR §2.2.2). */
  | { readonly kind: 'mulligan-bottom'; readonly defId: number }
  /** Stop altering: bottom what was chosen, draw back up. */
  | { readonly kind: 'mulligan-done' }
  /** Put a card from hand into your inkwell — once per turn (CR §4.2). */
  | { readonly kind: 'ink'; readonly defId: number }
  /**
   * The same once-per-turn ink action, sourced from the discard pile instead of hand — legal
   * only while a live effect makes the discard count as an origin (CR §4.2, §6.6.5; e.g. Moana
   * - Curious Explorer's ANCESTRAL LEGACY). A discard is public, so this is keyed by **instance
   * id**, unlike `ink`'s hidden-hand `defId`.
   */
  | { readonly kind: 'ink-from-discard'; readonly instance: InstanceId }
  /**
   * Play a card from hand (CR §4.3). `payment` is the **announced payment method** (CR
   * §4.3.1.2): absent means the ink cost, and every alternate cost — singing, Sing Together,
   * Shift, "for free" — is its own option with its own key, so choosing *how* to pay is never
   * a follow-up question. See `payments.ts`.
   */
  | { readonly kind: 'play'; readonly defId: number; readonly payment?: PaymentPlan }
  /**
   * Play a card from a public zone under a continuous zone-play permission (ticket 140) — The
   * Horned King - Merciless Master's CAULDRON'S POWER, "you may play characters from your
   * discard". Unlike `play`, the source zone (a discard) is public, so this is keyed by
   * **instance id**, not `defId`; `free` is the permission's own cost policy (never a player
   * choice between methods the way `play`'s `payment` is), so `false` still means paying the
   * card's full printed cost in ink, not an alternate cost.
   */
  | { readonly kind: 'play-from-zone'; readonly instance: InstanceId; readonly free: boolean }
  /** Quest with one of your dry, ready characters (CR §4.5). */
  | { readonly kind: 'quest'; readonly instance: InstanceId }
  /**
   * Move one of your characters to one of your locations, paying its move cost (CR §4.3.6).
   * Both cards are in play, so both are keyed by instance.
   */
  | { readonly kind: 'move'; readonly instance: InstanceId; readonly location: InstanceId }
  /** Declare a challenger (CR §4.6); the defender is the next choice. */
  | { readonly kind: 'challenge'; readonly instance: InstanceId }
  /** The declared challenger's target. */
  | { readonly kind: 'challenge-target'; readonly instance: InstanceId }
  /** Resolve this waiting triggered ability of yours next (CR §7.7.4); `item` is its bag id. */
  | { readonly kind: 'bag-order'; readonly item: number }
  /** Ready this eligible character, while a live ready-cap restriction picks among several. */
  | { readonly kind: 'ready-choose'; readonly instance: InstanceId }
  /**
   * Use an activated ability of a card you control (CR §4.4). `ability` is its index in the
   * card's ability list, so two abilities on one card are distinct options.
   *
   * `banish` is the cards a "Banish 2 of your items —" cost takes, `exert` the cards a
   * "⟳ one of your characters —" cost takes, and `discard` the hand cards a "Discard a card —"
   * cost takes: each payment is a *choice*, so — exactly as for playing a card — every legal
   * way to pay it is its own keyed option rather than a follow-up question (`payments.ts`).
   * `discard` names **printed ids**, the hand being hidden (SPEC.md §4.4).
   */
  | {
      readonly kind: 'activate';
      readonly instance: InstanceId;
      readonly ability: number;
      readonly banish?: readonly InstanceId[];
      readonly exert?: readonly InstanceId[];
      readonly discard?: readonly number[];
    }
  /** Pick this card for the ability's pending target (CR §6.7.3). */
  | { readonly kind: 'ability-target'; readonly instance: InstanceId }
  /** Stop picking: "up to N" chose fewer, or the pool is empty (CR §6.1.3). */
  | { readonly kind: 'ability-target-done' }
  /** Pick this player for the ability's pending `choose-player` (ticket 102). */
  | { readonly kind: 'choose-player'; readonly player: PlayerIndex }
  /**
   * Spend this much of the pool on the character just picked in a `distribute-remove-damage`
   * decision (ticket 135) — always between 1 and however much of the pool that character's
   * current damage can still absorb, so every option here is legal to spend in full.
   */
  | { readonly kind: 'ability-amount'; readonly value: number }
  /** Accept or decline a "you may" (CR §6.1.4). */
  | { readonly kind: 'may'; readonly accept: boolean }
  /** Pick this mode of a "choose one —" (CR §6.1.3). */
  | { readonly kind: 'ability-mode'; readonly mode: number }
  /**
   * Answer a `custom()` generator's decision. `token` is `i<instance>` for a card, the
   * reserved `none`, or the generator's own dense token — never `d<defId>`, which would put a
   * printed identity into the frame that records it (SPEC.md §4.4, §5.2).
   */
  | { readonly kind: 'custom-choice'; readonly token: string }
  /** Discard this card from your hand for a resolving effect (CR §7.3). */
  | { readonly kind: 'discard'; readonly defId: number }
  /** Declare the end of your turn (CR §3.3.2). */
  | { readonly kind: 'end-turn' };

export type ActionKind = Action['kind'];

/** One legal option: its canonical key and the action it stands for. */
export interface ChoiceOption {
  readonly key: string;
  readonly action: Action;
}

/** The one decision a state is waiting on. */
export interface ChoiceRequest {
  readonly kind: ChoiceKind;
  /** Who decides. `-1` only for `game-over`, which nobody answers. */
  readonly player: PlayerIndex;
  readonly options: readonly ChoiceOption[];
}

/** The canonical key for an action — see the module docs for the keying rules. */
export function actionKey(action: Action): string {
  switch (action.kind) {
    case 'mulligan-bottom':
      return `mulligan:bottom:d${action.defId}`;
    case 'mulligan-done':
      return 'mulligan:done';
    case 'ink':
      return `ink:d${action.defId}`;
    case 'ink-from-discard':
      return `ink-from-discard:i${action.instance}`;
    case 'play':
      return `play:d${action.defId}${paymentKey(action.payment)}`;
    case 'play-from-zone':
      return `play-from-zone:i${action.instance}${action.free ? ':free' : ''}`;
    case 'quest':
      return `quest:i${action.instance}`;
    case 'move':
      return `move:i${action.instance}:i${action.location}`;
    case 'challenge':
      return `challenge:i${action.instance}`;
    case 'challenge-target':
      return `challenge-target:i${action.instance}`;
    case 'bag-order':
      // The bag is a public zone (CR §7.7.1), so a bag item is keyed by its own dense id.
      return `bag-order:b${action.item}`;
    case 'ready-choose':
      return `ready-choose:i${action.instance}`;
    case 'activate':
      // The card is in play (public); the ability index is its own dense id on that card, and
      // a cost paid by banishing or exerting cards names them the way every other payment does.
      // A discard cost names hand cards, so — the hand being hidden — it is keyed by printed
      // id, exactly as the standalone `discard:d…` option is (SPEC.md §4.4).
      return (
        `activate:i${action.instance}:a${action.ability}` +
        (action.banish === undefined ? '' : `:banish:${instanceList(action.banish)}`) +
        (action.exert === undefined ? '' : `:exert:${instanceList(action.exert)}`) +
        (action.discard === undefined ? '' : `:discard:${defIdList(action.discard)}`)
      );
    case 'ability-target':
      return `ability-target:i${action.instance}`;
    case 'ability-target-done':
      return 'ability-target:done';
    case 'choose-player':
      return `choose-player:p${action.player}`;
    case 'ability-amount':
      return `ability-amount:${action.value}`;
    case 'may':
      return action.accept ? 'may:yes' : 'may:no';
    case 'ability-mode':
      return `ability-mode:m${action.mode}`;
    case 'discard':
      // A hand is hidden, so the option is keyed by printed id (SPEC.md §4.4).
      return `discard:d${action.defId}`;
    case 'custom-choice':
      // The token already follows the keying rules — `i<instance>` for a public card, the
      // generator's own dense id otherwise; `custom.ts` rejects a `d<defId>` token.
      return `custom:${action.token}`;
    case 'end-turn':
      return 'end-turn';
  }
}

/**
 * The key suffix for an announced payment method (CR §4.3.1.2).
 *
 * The ink cost is the unmarked default, so `play:d12` means the same thing it always did.
 * Every other method names the **public cards** it uses (in play, or in a discard), ascending
 * by instance id so the key is canonical however the option was built — a hand card is keyed
 * by `defId` in the prefix, and nothing a payment names is hidden, so a payment key never has
 * to be redacted.
 */
function paymentKey(payment: PaymentPlan | undefined): string {
  if (payment === undefined || payment.kind === 'ink') return '';
  switch (payment.kind) {
    case 'free':
      return ':free';
    case 'sing':
      return `:sing:${instanceList(payment.singers)}`;
    case 'shift':
      return `:shift:${instanceList(payment.onto)}`;
    case 'banish':
      return `:banish:${instanceList(payment.banish)}`;
    case 'exert':
      return `:exert:${instanceList(payment.exert)}`;
    case 'move-to-bottom-of-deck':
      return `:move-to-bottom-of-deck:${instanceList(payment.cards)}`;
    case 'shift-alternate-cost':
      return `:shift:${instanceList(payment.onto)}:${payment.method}:${instanceList(payment.cards)}`;
  }
}

function instanceList(ids: readonly InstanceId[]): string {
  return [...ids]
    .sort((a, b) => a - b)
    .map((id) => `i${id}`)
    .join('+');
}

/** Hidden-zone cards are keyed by printed id — `d5+d7`, ascending, duplicates kept. */
function defIdList(ids: readonly number[]): string {
  return [...ids]
    .sort((a, b) => a - b)
    .map((id) => `d${id}`)
    .join('+');
}

/** Pair an action with its canonical key. */
export function option(action: Action): ChoiceOption {
  return { key: actionKey(action), action };
}
